import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw/dist/leaflet.draw";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";
import "./EditableMap.css";
import defaultLeafletMarkerIcon from "../utils/leafletConfig";

const DEFAULT_CENTER = [36.25142, 10.25514];
const CIRCLE_SEGMENTS = 64;
const EARTH_RADIUS_METERS = 6371000;

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const closePolygon = (coordinates) => {
  if (coordinates.length === 0) return coordinates;

  const first = coordinates[0];
  const last = coordinates[coordinates.length - 1];
  if (first.Lat !== last.Lat || first.Long !== last.Long) {
    return [...coordinates, { ...first }];
  }

  return coordinates;
};

const parseInitialBoundary = (value) => {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) return [];

    const coordinates = parsed.map((point) => ({
      Lat: toFiniteNumber(point?.Lat ?? point?.lat ?? point?.[0]),
      Long: toFiniteNumber(point?.Long ?? point?.lng ?? point?.lon ?? point?.[1]),
    }));

    if (
      coordinates.length < 3 ||
      coordinates.some(
        (point) =>
          point.Lat === null ||
          point.Long === null ||
          point.Lat < -90 ||
          point.Lat > 90 ||
          point.Long < -180 ||
          point.Long > 180
      )
    ) {
      return [];
    }

    return closePolygon(coordinates);
  } catch (_error) {
    return [];
  }
};

const circleToPolygon = (center, radius, segments = CIRCLE_SEGMENTS) => {
  const latitude = (center.lat * Math.PI) / 180;
  const longitude = (center.lng * Math.PI) / 180;
  const angularDistance = radius / EARTH_RADIUS_METERS;
  const coordinates = [];

  for (let index = 0; index < segments; index += 1) {
    const bearing = (2 * Math.PI * index) / segments;
    const pointLatitude = Math.asin(
      Math.sin(latitude) * Math.cos(angularDistance) +
        Math.cos(latitude) * Math.sin(angularDistance) * Math.cos(bearing)
    );
    const pointLongitude =
      longitude +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitude),
        Math.cos(angularDistance) - Math.sin(latitude) * Math.sin(pointLatitude)
      );

    coordinates.push({
      Lat: (pointLatitude * 180) / Math.PI,
      Long: (pointLongitude * 180) / Math.PI,
    });
  }

  return closePolygon(coordinates);
};

const polygonCoordinatesFromLayer = (layer) => {
  const latLngGroups = layer.getLatLngs();
  const latLngs = Array.isArray(latLngGroups[0]) ? latLngGroups[0] : latLngGroups;

  return closePolygon(
    latLngs.map((point) => ({
      Lat: point.lat,
      Long: point.lng,
    }))
  );
};

const calculatePolygonArea = (coordinates) => {
  let area = 0;

  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const latitude1 = (coordinates[index].Lat * Math.PI) / 180;
    const latitude2 = (coordinates[index + 1].Lat * Math.PI) / 180;
    const longitudeDelta =
      ((coordinates[index + 1].Long - coordinates[index].Long) * Math.PI) / 180;

    area += longitudeDelta * (2 + Math.sin(latitude1) + Math.sin(latitude2));
  }

  return Math.round(Math.abs((area * EARTH_RADIUS_METERS ** 2) / 2));
};

const EditableMap = ({
  sensorsCoords,
  setCoords,
  setLayer,
  setSuperficie,
  setDimensions,
  initialLayer,
  initialCoords,
}) => {
  const { t } = useTranslation();
  const mapContainerRef = useRef(null);
  const initialLayerRef = useRef(initialLayer);
  const initialCoordsRef = useRef(initialCoords);

  useEffect(() => {
    if (!mapContainerRef.current) return undefined;

    const initialBoundary = parseInitialBoundary(initialLayerRef.current);
    const initialLatitude = toFiniteNumber(initialCoordsRef.current?.Latitude);
    const initialLongitude = toFiniteNumber(initialCoordsRef.current?.Longitude);
    const firstSensor = Array.isArray(sensorsCoords)
      ? sensorsCoords.find(
          (sensor) =>
            toFiniteNumber(sensor?.Latitude) !== null &&
            toFiniteNumber(sensor?.Longitude) !== null
        )
      : null;

    let mapCenter = DEFAULT_CENTER;
    if (initialLatitude !== null && initialLongitude !== null) {
      mapCenter = [initialLatitude, initialLongitude];
    } else if (initialBoundary.length > 0) {
      mapCenter = [initialBoundary[0].Lat, initialBoundary[0].Long];
    } else if (firstSensor) {
      mapCenter = [Number(firstSensor.Latitude), Number(firstSensor.Longitude)];
    }

    const map = L.map(mapContainerRef.current).setView(mapCenter, 15);

    L.tileLayer("https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}", {
      subdomains: ["mt0", "mt1", "mt2", "mt3"],
    }).addTo(map);

    const editableLayers = new L.FeatureGroup().addTo(map);

    const updateFormFromLayer = (shapeLayer) => {
      let coordinates = [];
      let latitude;
      let longitude;
      let area;

      if (shapeLayer instanceof L.Circle) {
        const center = shapeLayer.getLatLng();
        const radius = shapeLayer.getRadius();
        coordinates = circleToPolygon(center, radius);
        latitude = center.lat;
        longitude = center.lng;
        area = Math.round(Math.PI * radius ** 2);

        if (setDimensions) {
          const diameter = Math.round(radius * 2);
          setDimensions({ width: diameter, length: diameter, radius: Math.round(radius) });
        }
      } else if (shapeLayer instanceof L.Polygon) {
        coordinates = polygonCoordinatesFromLayer(shapeLayer);
        const center = shapeLayer.getBounds().getCenter();
        latitude = center.lat;
        longitude = center.lng;
        area = calculatePolygonArea(coordinates);
        if (setDimensions) setDimensions(null);
      }

      if (coordinates.length < 4) return;

      setLayer(JSON.stringify(coordinates));
      setCoords({ Latitude: latitude, Longitude: longitude });
      if (setSuperficie) setSuperficie(area);
    };

    const registerEditableLayer = (shapeLayer) => {
      shapeLayer.on("edit move resize", () => updateFormFromLayer(shapeLayer));
    };

    if (initialBoundary.length > 0) {
      const polygon = L.polygon(
        initialBoundary.map((point) => [point.Lat, point.Long]),
        { color: "blue" }
      );
      editableLayers.addLayer(polygon);
      registerEditableLayer(polygon);
      map.fitBounds(polygon.getBounds(), { padding: [20, 20] });
    }

    const drawControl = new L.Control.Draw({
      position: "topright",
      draw: {
        circle: {
          shapeOptions: { color: "blue" },
          showRadius: true,
          metric: true,
        },
        circlemarker: false,
        marker: false,
        polyline: false,
        rectangle: false,
        polygon: {
          allowIntersection: false,
          drawError: { color: "red", timeout: 1000 },
          shapeOptions: { color: "blue" },
        },
      },
      edit: {
        featureGroup: editableLayers,
        edit: true,
        remove: true,
      },
    });
    map.addControl(drawControl);

    const locationMarker = L.marker(mapCenter, { icon: defaultLeafletMarkerIcon })
      .addTo(map)
      .bindPopup(t("field_location", { defaultValue: "Field location" }));

    map.on(L.Draw.Event.CREATED, (event) => {
      editableLayers.clearLayers();
      editableLayers.addLayer(event.layer);
      registerEditableLayer(event.layer);
      updateFormFromLayer(event.layer);

      // Keep the radius handle available immediately on touch devices.
      if (event.layer instanceof L.Circle && event.layer.editing) {
        event.layer.editing.enable();
      }
    });

    map.on(L.Draw.Event.EDITED, (event) => {
      event.layers.eachLayer(updateFormFromLayer);
    });

    map.on(L.Draw.Event.DELETED, () => {
      setLayer("");
      setCoords({ Latitude: "", Longitude: "" });
      if (setSuperficie) setSuperficie(null);
      if (setDimensions) setDimensions(null);
    });

    const geocoder = L.Control.Geocoder.latLng({
      next: L.Control.Geocoder.nominatim(),
    });

    L.Control.geocoder({ defaultMarkGeocode: false, geocoder })
      .on("markgeocode", (event) => {
        const latLng = event.geocode.center;
        locationMarker
          .setLatLng(latLng)
          .bindPopup(event.geocode.name)
          .openPopup();
        map.fitBounds(event.geocode.bbox);
      })
      .addTo(map);

    return () => {
      map.remove();
    };
  }, [sensorsCoords, setCoords, setDimensions, setLayer, setSuperficie]);

  return <div ref={mapContainerRef} style={{ height: "400px" }} />;
};

export default EditableMap;
